import React, { useCallback, useEffect, useRef, useState } from "react";
import { C, font, mono } from "../lib/styles.js";
import { LogoMark } from "./Logo.jsx";
import { styles as base } from "./LoginScreen.jsx";
import * as flow from "../lib/authFlow.js";
import { cleanCode, enrollErrorText, formatKey, isFullCode } from "../lib/passwordSignIn.js";

/*
  First-time set-up of the authenticator (docs/ops/password-signin.md).
  App.jsx shows this, and nothing else, to a password sign-in that has no
  authenticator while the switch says "required". The QR code is drawn here
  from Firebase's secret (the qrcode package, loaded on demand): the secret
  never leaves the browser except to Firebase. Firebase needs a verified
  email to set one up (the account tool marks it verified).
*/

async function qrDataUrl(uri) {
  const mod = await import("qrcode");
  const QRCode = mod.default ?? mod;
  return QRCode.toDataURL(uri, { width: 232, margin: 1, errorCorrectionLevel: "M", color: { dark: "#111111", light: "#FFFFFF" } });
}

export default function AuthenticatorSetup({ user, onEnrolled, onSignOut }) {
  const [phase, setPhase] = useState("starting"); // starting | ready | saving | failed
  const [setup, setSetup] = useState(null); // { secret, key, uri }
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState("");
  const [message, setMessage] = useState(null);
  const [mustSignIn, setMustSignIn] = useState(false);
  const started = useRef(false);

  const start = useCallback(async () => {
    setPhase("starting");
    setMessage(null);
    setMustSignIn(false);
    setCode("");
    setQr(null);
    try {
      const s = await flow.startAuthenticatorSetup(user);
      setSetup(s);
      setPhase("ready");
      try {
        setQr(await qrDataUrl(s.uri));
      } catch {
        setQr(null); // the key below still works
      }
    } catch (e) {
      const r = enrollErrorText(e?.code);
      setMessage(r.text);
      setMustSignIn(r.signOut);
      setPhase("failed");
    }
  }, [user]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    start();
  }, [start]);

  const finish = async (e) => {
    e.preventDefault();
    if (!isFullCode(code)) {
      setMessage("Enter the 6-digit code the app shows.");
      return;
    }
    setPhase("saving");
    setMessage(null);
    try {
      await flow.finishAuthenticatorSetup(user, setup.secret, code);
      onEnrolled();
    } catch (err) {
      const r = enrollErrorText(err?.code);
      setMessage(r.text);
      setCode("");
      setMustSignIn(Boolean(r.signOut));
      // Only a fresh sign-in or a new set-up code helps: the form goes, and
      // the one button that helps takes its place.
      setPhase(r.signOut || r.restart ? "failed" : "ready");
    }
  };

  return (
    <SetupView
      email={user?.email || ""}
      phase={phase}
      qr={qr}
      setupKey={setup?.key || ""}
      uri={setup?.uri || ""}
      code={code}
      message={message}
      mustSignIn={mustSignIn}
      onCode={(v) => setCode(cleanCode(v))}
      onSubmit={finish}
      onRestart={start}
      onSignOut={onSignOut}
    />
  );
}

export function SetupView({ email, phase, qr, setupKey, uri, code, message, mustSignIn, onCode, onSubmit, onRestart, onSignOut }) {
  const busy = phase === "saving" || phase === "starting";
  return (
    <div className="signin-screen" style={base.screen}>
      <div style={{ ...base.panel, maxWidth: 420 }}>
        <div style={base.brand}>
          <LogoMark size={36} />
          <span style={base.brandName}>NYC Suite</span>
        </div>
        <div style={{ ...base.stack, marginTop: 20 }}>
          <h1 style={base.title}>Set up your authenticator</h1>
          <p style={base.text}>
            From now on, every sign-in to the Suite asks for a 6-digit code from an app on your own phone. Setting
            it up takes a minute and happens once.
          </p>

          {phase === "starting" && <p style={base.hint} role="status">Preparing your set-up code…</p>}

          {phase === "failed" && (
            <>
              {mustSignIn ? (
                <button type="button" style={base.primary} onClick={onSignOut}>Sign in again</button>
              ) : (
                <button type="button" style={base.primary} onClick={onRestart}>Start again</button>
              )}
            </>
          )}

          {(phase === "ready" || phase === "saving") && (
            <>
              <Step n={1}>Install Google Authenticator (or any authenticator app) on your own phone and scan this.</Step>
              <div style={s.qrBox}>
                {qr ? (
                  <img src={qr} alt="Set-up code for your authenticator app" width={232} height={232} style={s.qr} />
                ) : (
                  <div style={s.qrMissing}>Use the key below instead.</div>
                )}
              </div>
              <p style={base.hint}>Can't scan? In the app, choose to enter a setup key, and type:</p>
              <div style={s.key} aria-label="Setup key">{formatKey(setupKey)}</div>
              <p style={base.hint}>Account: {email}. Type: time based.</p>
              {uri && (
                <a href={uri} style={s.appLink}>Setting up on this same phone? Tap here to add it to your authenticator app.</a>
              )}

              <form style={{ ...base.stack, marginTop: 8 }} onSubmit={onSubmit} noValidate>
                <Step n={2}>Enter the 6-digit code the app now shows for NYC Suite.</Step>
                <label style={base.label} htmlFor="setup-code">6-digit code</label>
                <input
                  id="setup-code" name="code" type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code"
                  maxLength={7} value={code} onChange={(e) => onCode(e.target.value)}
                  style={{ ...base.input, ...base.codeInput }} disabled={busy}
                />
                <button type="submit" style={base.primary} disabled={busy}>{phase === "saving" ? "Checking…" : "Finish setup"}</button>
              </form>
            </>
          )}

          {message && <div role="alert" style={{ ...base.error, marginTop: 4 }}>{message}</div>}

          <p style={base.hint}>
            Keep that phone: you need its code every time you sign in with a password. Lost it? Ivin can reset your
            authenticator.
          </p>
          <div style={s.footer}>
            <span style={s.who}>Signed in as {email}</span>
            <button type="button" style={base.link} onClick={onSignOut}>Sign out</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <p style={{ ...base.text, display: "flex", gap: 10, alignItems: "baseline" }}>
      <span style={s.stepNo} aria-hidden="true">{n}</span>
      <span>{children}</span>
    </p>
  );
}

const s = {
  qrBox: { display: "flex", justifyContent: "center", padding: "8px 0" },
  qr: { width: 232, height: 232, maxWidth: "100%", border: `1px solid ${C.border}`, borderRadius: 8, background: "#FFF" },
  qrMissing: { width: 232, height: 120, display: "grid", placeItems: "center", border: `1px dashed ${C.border}`, borderRadius: 8, color: C.muted, fontSize: 14 },
  key: {
    fontFamily: mono,
    fontSize: 16,
    lineHeight: 1.6,
    letterSpacing: "0.04em",
    padding: "10px 12px",
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    background: C.surface,
    color: C.text,
    wordBreak: "break-all",
    userSelect: "all",
    WebkitUserSelect: "all",
  },
  appLink: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 44,
    color: C.text,
    fontSize: 14,
    lineHeight: 1.4,
    fontFamily: font,
  },
  stepNo: {
    flex: "none",
    width: 22,
    height: 22,
    borderRadius: 11,
    background: C.text,
    color: "#FFF",
    fontSize: 12,
    display: "inline-grid",
    placeItems: "center",
    transform: "translateY(-1px)",
  },
  footer: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", borderTop: `1px solid ${C.border}`, paddingTop: 8 },
  who: { fontSize: 13, color: C.muted, overflowWrap: "anywhere" },
};
